import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useCreateDeviceGroup, DeviceGroup } from "@/hooks/use-device-groups";
import { useSimpleRole } from "@/hooks/use-simple-role";
import { useUserOrganization } from "@/hooks/use-user-organization";
import { Loader2 } from "lucide-react";

interface CreateGroupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId?: string | null;
  onGroupCreated?: (group: DeviceGroup) => void;
}

export const CreateGroupDialog = ({
  open,
  onOpenChange,
  organizationId,
  onGroupCreated,
}: CreateGroupDialogProps) => {
  const [name, setName] = useState("");
  const [reference, setReference] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState("#6366f1");

  const { role } = useSimpleRole();
  const { data: userOrgId } = useUserOrganization();
  const isSuperuser = role === 'superuser';
  
  // For non-superusers, always use their org; for superusers, use provided or null
  // Use the organisation passed in (camera's org); fall back to the user's own org
  const effectiveOrgId = organizationId ?? (isSuperuser ? null : userOrgId) ?? null;
  
  const createGroup = useCreateDeviceGroup();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) return;

    try {
      const newGroup = await createGroup.mutateAsync({
        name: name.trim(),
        organization_id: effectiveOrgId,
        reference: reference.trim() || null,
        description: description.trim() || null,
        color: color || null,
      });

      // Reset form
      setName("");
      setReference("");
      setDescription("");
      setColor("#6366f1");

      onOpenChange(false);
      onGroupCreated?.(newGroup);
    } catch (error) {
      // Error handled in hook
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Create New Group</DialogTitle>
          <DialogDescription>
            Create a new device group for organizing your devices.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Group Name *</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Main Entrance, Golf Course"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="reference">Reference (Optional)</Label>
            <Input
              id="reference"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="e.g., GRP-001"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description (Optional)</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of this group"
              className="min-h-[60px]"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="color">Color (Optional)</Label>
            <div className="flex items-center gap-2">
              <Input
                id="color"
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="w-12 h-10 p-1 cursor-pointer"
              />
              <Input
                value={color}
                onChange={(e) => setColor(e.target.value)}
                placeholder="#6366f1"
                className="flex-1"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || createGroup.isPending}>
              {createGroup.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Create Group
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
